import { getAudioContext, isAudioRunning, resumeAudioContext } from './audioContext'
export type GameSoundEffect = 'click' | 'ding' | 'death' | 'fate' | 'victory' | 'birth' | 'coin' | 'stamp' | 'scold' | 'sweep' | 'rewind' | 'incoming' | 'boom' | 'achieve'


const NOTES = Object.freeze({
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392,
  A4: 440, B4: 493.88, C5: 523.25, D5: 587.33, E5: 659.25,
  F5: 698.46, G5: 783.99, A5: 880, B5: 987.77
})

type NoteName = keyof typeof NOTES

// The original index.html melody and rhythm, retained for returning players.
const MELODY: NoteName[] = [
  'C5', 'E5', 'G5', 'E5', 'A5', 'G5', 'E5', 'C5', 'D5', 'F5',
  'A5', 'F5', 'B4', 'D5', 'G5', 'D5', 'C5', 'G4', 'E5', 'G5'
]

const STEP_SECONDS = 0.19
const MASTER_VOLUME = 0.82

let context: AudioContext | null = null
let master: GainNode | null = null
let bgmTimer: number | null = null
let bgmIndex = 0
let bgmWanted = false
let enabled = true

function ensureContext(): AudioContext | null {
  const audio = getAudioContext()
  if (!audio) return null
  if (audio === context && master) return audio
  try {
    master?.disconnect()
    context = audio
    master = audio.createGain()
    master.gain.value = enabled ? MASTER_VOLUME : 0
    master.connect(audio.destination)
    audio.addEventListener?.('statechange', () => {
      if (audio !== context) return
      if (!isAudioRunning(audio)) cancelBgmTimer()
      else if (enabled && bgmWanted && bgmTimer === null && !document.hidden) playBgmStep()
    })
    return audio
  } catch {
    context = null
    master = null
    return null
  }
}

function tone(frequency: number, duration: number, type: OscillatorType, volume: number, when?: number, slide?: number): void {
  if (!enabled) return
  const audio = ensureContext()
  if (!audio || !master || !isAudioRunning(audio) || document.hidden) return
  try {
    const oscillator = audio.createOscillator()
    const gain = audio.createGain()
    const start = when ?? audio.currentTime
    oscillator.type = type
    oscillator.frequency.setValueAtTime(frequency, start)
    if (slide) oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, slide), start + duration)
    gain.gain.setValueAtTime(0.0001, start)
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration)
    oscillator.connect(gain)
    gain.connect(master)
    oscillator.onended = () => {
      try { oscillator.disconnect() } catch { /* context may already be closed */ }
      try { gain.disconnect() } catch { /* context may already be closed */ }
    }
    oscillator.start(start)
    oscillator.stop(start + duration + 0.03)
  } catch {
    // Audio is optional; a partial Web Audio implementation must not stop play.
  }
}

function cancelBgmTimer(): void {
  if (bgmTimer !== null) window.clearTimeout(bgmTimer)
  bgmTimer = null
}

function playBgmStep(): void {
  if (!enabled || !bgmWanted || document.hidden) {
    cancelBgmTimer()
    return
  }
  const audio = ensureContext()
  if (!audio || !isAudioRunning(audio)) { cancelBgmTimer(); return }
  const note = MELODY[bgmIndex % MELODY.length]
  tone(NOTES[note], STEP_SECONDS * 0.9, 'triangle', 0.07)
  if (bgmIndex % 8 === 0) tone(NOTES.C4, STEP_SECONDS * 7, 'sine', 0.045)
  bgmIndex += 1
  bgmTimer = window.setTimeout(playBgmStep, STEP_SECONDS * 1000)
}

export function startGameBgm(): void {
  bgmWanted = true
  if (!enabled || document.hidden) return
  const audio = ensureContext()
  if (!audio) return
  master!.gain.value = MASTER_VOLUME
  void resumeAudioContext(audio).then(() => {
    if (audio === context && isAudioRunning(audio) && enabled && bgmWanted && bgmTimer === null && !document.hidden) playBgmStep()
  })
}

export function stopGameBgm(): void {
  bgmWanted = false
  bgmIndex = 0
  cancelBgmTimer()
}

export function unlockGameAudio(): void {
  if (!enabled || document.hidden) return
  const audio = ensureContext()
  if (!audio) return
  void resumeAudioContext(audio).then(() => {
    if (audio === context && isAudioRunning(audio) && enabled && bgmWanted && bgmTimer === null && !document.hidden) playBgmStep()
  })
}

export function setGameAudioEnabled(next: boolean): void {
  enabled = next
  if (master) master.gain.value = next ? MASTER_VOLUME : 0
  if (!next) cancelBgmTimer()
  else if (bgmWanted) startGameBgm()
}

export function playGameSound(effect: GameSoundEffect): void {
  if (!enabled) return
  const audio = ensureContext()
  if (!audio) return
  void resumeAudioContext(audio)
  const now = audio.currentTime
  if (effect === 'click') {
    tone(700, 0.07, 'square', 0.13, now)
    tone(950, 0.06, 'square', 0.09, now + 0.02)
  } else if (effect === 'ding') {
    tone(523, 0.09, 'sine', 0.17, now)
    tone(784, 0.2, 'sine', 0.15, now + 0.08)
  } else if (effect === 'death') {
    tone(400, 0.5, 'sawtooth', 0.15, now, 70)
    tone(200, 0.6, 'square', 0.09, now + 0.05, 55)
  } else if (effect ==='achieve'){[659,784,988].forEach(function(f: number,i: number){tone(f,0.15,'triangle',0.14,now+i*0.09);});
  } else if (effect ==='coin'){tone(1319,0.07,'sine',0.13,now);tone(1760,0.16,'sine',0.11,now+0.06);tone(2217,0.12,'sine',0.05,now+0.13);
  } else if (effect ==='stamp'){tone(90,0.2,'square',0.22,now,55);tone(58,0.28,'sine',0.18,now+0.03,30);
  } else if (effect ==='scold'){tone(220,0.16,'sawtooth',0.2,now,120);tone(160,0.3,'square',0.16,now+0.12,70);tone(110,0.35,'sawtooth',0.18,now+0.26,55);
  } else if (effect ==='sweep'){tone(320,0.16,'sawtooth',0.14,now,240);tone(150,0.24,'square',0.1,now+0.05,80);
  } else if (effect ==='rewind'){[900,800,700,620,540,470,400,350,300,260].forEach(function(f: number,i: number){tone(f,0.05,'square',0.05,now+i*0.055);});
  } else if (effect ==='incoming'){tone(1400,0.85,'sine',0.09,now,300);tone(900,0.85,'sine',0.05,now+0.05,220);
  } else if (effect ==='boom'){tone(90,0.9,'sawtooth',0.22,now,28);tone(50,1.2,'square',0.12,now+0.06,20);tone(200,0.3,'sawtooth',0.14,now,60);
  } else if (effect === 'fate') {
    tone(220, 0.7, 'sine', 0.12, now, 660)
  } else {
    const notes = effect === 'victory' ? [523, 659, 784, 1047, 1319] : [392, 523, 659, 784]
    notes.forEach((frequency, index) => tone(frequency, effect === 'victory' ? 0.2 : 0.18, 'triangle', effect === 'victory' ? 0.15 : 0.12, now + index * (effect === 'victory' ? 0.11 : 0.12)))
  }
}
